import dayjs from 'dayjs';
import { Type } from 'class-transformer';
import { BaseType } from './model';
import type { PartnerBase } from './partner-base';
import { TransformStringToDayJS, TransformStringToNumber } from './utils';
import type { OutreachChannel } from './types';
import { defaultTimezone } from '@ui/components/time/tz';

/**
 * Invoice statistics for a partner.
 */
export class PartnerInvoiceStats {
  @TransformStringToNumber()
  invoice_balance: number;

  @TransformStringToNumber()
  total_overdue: number;

  num_overdue_invoices: number;
  num_open_invoices: number;

  @TransformStringToDayJS()
  oldest_open_invoice_due_date: dayjs.Dayjs | null;
}

/**
 * Contact information completeness for partner table UI.
 */
export class ContactCompleteness {
  has_primary_email: boolean;
  has_primary_phone: boolean;
  is_email_disabled: boolean;
  is_phone_disabled: boolean;
}

/**
 * Minimal partner model optimized for table list views.
 * Contains only essential fields (~90% size reduction vs full Partner model).
 *
 * Use for: Partners table, Partner group table
 * Use full Partner model for: Detail pages, forms, nested data
 */
export class PartnerListSummary extends BaseType implements PartnerBase {
  // Identity
  id: string;
  name: string;
  legal_name: string | null;
  account_number: string | null;
  email: string | null;
  remote_id: string;
  currency: string | null;
  active_dispute_count: number;
  terminal_dispute_count: number;

  @TransformStringToDayJS()
  created_at: dayjs.Dayjs;

  // Relationships
  assignee_id: string | null;
  parent_id: string | null;
  business_unit_id: string | null;
  subsidiary_count: number;

  // Outreach - flat (direct properties)
  @TransformStringToDayJS()
  last_outreach_at: dayjs.Dayjs | null;
  last_outreach_to: string | null;
  last_outreach_channel: OutreachChannel | null;
  is_email_outreach_manually_paused: boolean;
  is_call_outreach_manually_paused: boolean;

  // Aggregates - nested
  @Type(() => PartnerInvoiceStats)
  invoice_stats: PartnerInvoiceStats;

  @Type(() => ContactCompleteness)
  contact_completeness: ContactCompleteness;

  // Computed Properties

  /**
   * Returns the best available name for display.
   * Fallback order: name → legal_name → account_number → email → id
   */
  get computedName(): string {
    return this.name || this.legal_name || this.account_number || this.email || this.id;
  }

  /**
   * Returns true if this partner is a group parent (has subsidiaries).
   */
  get isGroupParent(): boolean {
    return this.subsidiary_count > 0;
  }

  /**
   * Returns true if this partner is a group child (has a parent).
   */
  get isGroupChild(): boolean {
    return Boolean(this.parent_id);
  }

  /**
   * Returns true if this partner is part of a group (parent or child).
   */
  get isGrouped(): boolean {
    return this.isGroupParent || this.isGroupChild;
  }

  /**
   * Returns true if outreach is paused for this partner (email or call).
   */
  get isOutreachPaused(): boolean {
    return this.is_email_outreach_manually_paused || this.is_call_outreach_manually_paused;
  }

  /**
   * Returns true if this partner has an assignee.
   */
  get hasAssignee(): boolean {
    return Boolean(this.assignee_id);
  }

  /**
   * Returns true if this partner has complete contact info (email and phone).
   */
  get hasCompleteContactInfo(): boolean {
    return (
      this.contact_completeness.has_primary_email && this.contact_completeness.has_primary_phone
    );
  }

  /**
   * Returns the number of days overdue based on oldest open invoice due date.
   * Returns 0 if not overdue.
   */
  get daysOverdue(): number {
    if (this.invoice_stats.total_overdue <= 0) {
      return 0;
    }

    const dueDate = this.invoice_stats.oldest_open_invoice_due_date;
    if (!dueDate?.isValid()) {
      return 0;
    }

    const dueDateStart = dueDate.tz(defaultTimezone).startOf('day');
    const currentDay = dayjs().tz(defaultTimezone).startOf('day');
    return currentDay.diff(dueDateStart, 'days');
  }
}
